import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import * as FaIcons from 'react-icons/fa'
import PropTypes from 'prop-types'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('es', es)

// Formatea un Date a 'yyyy-mm-dd' en hora local (evita el corrimiento de día que
// produce toISOString() al usar UTC)
const toFechaString = (date) => {
    if (!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const RecargarGeosortModal = ({ show, onHide, onReload, loading }) => {
    const [fecha, setFecha] = useState(null)
    const [error, setError] = useState(null)
    const [submitting, setSubmitting] = useState(false)

    // Limpiar formulario cuando se cierra el modal
    useEffect(() => {
        if (!show) {
            setFecha(null)
            setError(null)
            setSubmitting(false)
        }
    }, [show])

    const handleClose = () => {
        if (!submitting) {
            onHide()
        }
    }

    const handleSubmit = async () => {
        if (!fecha) {
            setError('Debe seleccionar la fecha de GeoSort a recargar')
            return
        }

        setError(null)
        setSubmitting(true)
        try {
            await onReload(toFechaString(fecha))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            backdrop={submitting ? 'static' : true}
            keyboard={!submitting}>
            <Modal.Header closeButton={!submitting}>
                <Modal.Title>
                    <FaIcons.FaSyncAlt className="me-2" />
                    Recargar Fecha GeoSort
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Alert variant="info">
                    <FaIcons.FaInfoCircle className="me-2" />
                    Vuelve a descargar el reporte diario de GeoSort (rutas/guías de Falabella) desde
                    la plataforma externa y reprocesa esa fecha en Spread. Útil cuando un día de
                    GeoSort quedó mal cargado (rutas sin TMS/guías) y hay que reintentarlo.
                </Alert>

                <Form.Group className="mb-3">
                    <Form.Label>Fecha a recargar *</Form.Label>
                    <DatePicker
                        selected={fecha}
                        onChange={(date) => {
                            setFecha(date)
                            if (error) setError(null)
                        }}
                        dateFormat="dd/MM/yyyy"
                        locale="es"
                        maxDate={new Date()}
                        placeholderText="Seleccionar fecha de GeoSort"
                        className={`form-control ${error ? 'is-invalid' : ''}`}
                        disabled={submitting}
                    />
                    {error && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                            {error}
                        </div>
                    )}
                </Form.Group>

                <Alert variant="warning" className="mb-0">
                    <FaIcons.FaClock className="me-2" />
                    El reproceso corre en dos pasos (descarga del reporte + carga masiva) y el
                    segundo paso queda corriendo en segundo plano: el cambio puede tardar unos
                    <strong> 5 minutos</strong> en verse reflejado en el sistema.
                </Alert>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    Cancelar
                </Button>
                <Button
                    variant="warning"
                    onClick={handleSubmit}
                    disabled={submitting || loading || !fecha}>
                    {submitting ? (
                        <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Procesando...</span>
                            </div>
                            Recargando...
                        </>
                    ) : (
                        <>
                            <FaIcons.FaSyncAlt className="me-2" />
                            Recargar GeoSort
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

RecargarGeosortModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onReload: PropTypes.func.isRequired,
    loading: PropTypes.bool
}

RecargarGeosortModal.defaultProps = {
    loading: false
}

export default RecargarGeosortModal
